/**
 * Los datos de pago del proveedor (03/11): cuentas, pago móvil, Zelle, Binance.
 *
 * ⚠ **Cómo se ENSEÑA un dato lo arma el servidor** (`titulo`, `lineas`, `texto`): la
 * ficha, el modal de pago y la orden aprobada pintan esas líneas y «Copiar» copia ese
 * texto. Aquí sólo vive lo que necesita el FORMULARIO —qué casillas tiene cada clase—;
 * qué es obligatorio lo vuelve a decidir el servidor.
 */

export type TipoDatoPago =
  | "CUENTA_NACIONAL"
  | "PAGO_MOVIL"
  | "ZELLE"
  | "BINANCE"
  | "BILLETERA"
  | "CUENTA_EXTRANJERA"
  | "OTRO"

export type CampoDatoPago =
  | "banco_id"
  | "banco_nombre"
  | "numero_cuenta"
  | "tipo_cuenta"
  | "titular"
  | "documento"
  | "telefono"
  | "correo"
  | "identificador"
  | "direccion"
  | "red"
  | "swift"
  | "codigo_ruta"
  | "plataforma"
  | "notas"

export type DatoPago = Record<Exclude<CampoDatoPago, "banco_id">, string | null> & {
  id: number
  tipo: TipoDatoPago
  banco_id: number | null
  preferido: boolean
  creado_en: string
  titulo: string
  lineas: { rotulo: string; valor: string }[]
  texto: string
  /** Lo que hay que revisar antes de pagar (lo traído del 2.0 no pasó por el formulario). */
  avisos: string[]
}

export type DatosPagoProveedor = {
  proveedor_id: number
  nombre: string
  rif: string | null
  datos: DatoPago[]
  /** Por método de pago activo, qué clases de dato enseñar. Vacío: no usa datos del proveedor. */
  uso_por_metodo: Record<string, TipoDatoPago[]>
}

export const NOMBRE_TIPO: Record<TipoDatoPago, string> = {
  CUENTA_NACIONAL: "Cuenta bancaria",
  PAGO_MOVIL: "Pago móvil",
  ZELLE: "Zelle",
  BINANCE: "Binance",
  BILLETERA: "Billetera cripto",
  CUENTA_EXTRANJERA: "Cuenta en el exterior",
  OTRO: "Otra plataforma",
}

export const TIPOS: TipoDatoPago[] = Object.keys(NOMBRE_TIPO) as TipoDatoPago[]

export type Casilla = {
  campo: CampoDatoPago
  etiqueta: string
  ayuda?: string
  /** Se marca en la etiqueta; el servidor es quien lo exige. */
  obligatorio?: boolean
  numerica?: boolean
  ancha?: boolean
}

const TITULAR: Casilla = {
  campo: "titular",
  etiqueta: "Titular",
  ayuda: "Vacío: el proveedor",
}
const DOCUMENTO: Casilla = {
  campo: "documento",
  etiqueta: "Cédula o RIF del titular",
  ayuda: "Vacío: el RIF del proveedor",
}

/** Las casillas de cada clase, en el orden en que las pide el banco. */
export const CASILLAS: Record<TipoDatoPago, Casilla[]> = {
  CUENTA_NACIONAL: [
    {
      campo: "numero_cuenta",
      etiqueta: "Número de cuenta",
      ayuda: "20 dígitos; el banco sale de los cuatro primeros",
      obligatorio: true,
      numerica: true,
      ancha: true,
    },
    { campo: "banco_id", etiqueta: "Banco" },
    { campo: "tipo_cuenta", etiqueta: "Tipo de cuenta" },
    TITULAR,
    DOCUMENTO,
  ],
  PAGO_MOVIL: [
    { campo: "banco_id", etiqueta: "Banco", obligatorio: true },
    { campo: "telefono", etiqueta: "Teléfono", ayuda: "04XX-1234567", obligatorio: true },
    DOCUMENTO,
    TITULAR,
  ],
  ZELLE: [
    { campo: "correo", etiqueta: "Correo de Zelle", ayuda: "O el teléfono" },
    { campo: "telefono", etiqueta: "Teléfono de Zelle" },
    TITULAR,
  ],
  BINANCE: [
    { campo: "identificador", etiqueta: "Pay ID o usuario", ayuda: "O el correo" },
    { campo: "correo", etiqueta: "Correo de Binance" },
    TITULAR,
  ],
  BILLETERA: [
    {
      campo: "direccion",
      etiqueta: "Dirección de la billetera",
      obligatorio: true,
      ancha: true,
    },
    {
      campo: "red",
      etiqueta: "Red",
      ayuda: "TRC20, BEP20, ERC20… Por otra red, el dinero se pierde",
      obligatorio: true,
    },
  ],
  CUENTA_EXTRANJERA: [
    { campo: "banco_nombre", etiqueta: "Banco", obligatorio: true },
    { campo: "numero_cuenta", etiqueta: "Cuenta o IBAN", obligatorio: true },
    { campo: "swift", etiqueta: "SWIFT / BIC" },
    { campo: "codigo_ruta", etiqueta: "ABA / routing" },
    TITULAR,
  ],
  OTRO: [
    {
      campo: "plataforma",
      etiqueta: "Plataforma",
      ayuda: "PayPal, Zinli, Wally…",
      obligatorio: true,
    },
    { campo: "identificador", etiqueta: "Cuenta", obligatorio: true },
    TITULAR,
  ],
}

/**
 * Los datos que sirven a un método, según lo que dijo el servidor. Sin método elegido
 * —o uno que el servidor no conoce— se enseñan todos: esconder un dato a quien va a
 * pagar es peor que enseñarle uno de más. `null` es «el método no usa datos».
 */
export function paraElMetodo(
  respuesta: DatosPagoProveedor,
  metodoId: number | null | undefined,
): DatoPago[] | null {
  if (metodoId === null || metodoId === undefined) return respuesta.datos
  const tipos = respuesta.uso_por_metodo[String(metodoId)]
  if (tipos === undefined) return respuesta.datos
  if (tipos.length === 0) return null
  return respuesta.datos
    .filter((d) => tipos.includes(d.tipo))
    .sort((a, b) => tipos.indexOf(a.tipo) - tipos.indexOf(b.tipo))
}
